import { useState, useEffect } from 'react';
import {
  Mail,
  Loader2,
  Save,
  Check,
  Send,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { formatDisplayDateTime } from '@/lib/date-format';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useEmailSettings,
  useUpdateEmailSettings,
  useTestEmail,
} from '@/hooks/use-email-settings';
import { toast } from '@/components/ui/toaster';
import { useAuth } from '@/hooks/use-auth';

export function EmailSettingsCard() {
  const { user } = useAuth();
  const { data: settings, isLoading } = useEmailSettings();
  const updateSettings = useUpdateEmailSettings();
  const testEmail = useTestEmail();

  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpSecure, setSmtpSecure] = useState(true);
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (settings) {
      setSmtpHost(settings.smtpHost);
      setSmtpPort(String(settings.smtpPort));
      setSmtpUser(settings.smtpUser);
      setSmtpSecure(settings.smtpSecure);
      setSenderName(settings.senderName);
      setSenderEmail(settings.senderEmail);
      setIsEnabled(settings.isEnabled);
      setSmtpPassword('');
    }
  }, [settings]);

  useEffect(() => {
    if (user?.email) {
      setTestRecipient(user.email);
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!smtpHost || !smtpPort || !smtpUser || !senderName || !senderEmail) {
      toast({ title: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }

    if (!settings && !smtpPassword) {
      toast({ title: 'SMTP password is required', variant: 'destructive' });
      return;
    }

    const port = parseInt(smtpPort, 10);
    if (isNaN(port) || port < 1 || port > 65535) {
      toast({ title: 'Invalid port number', variant: 'destructive' });
      return;
    }

    try {
      await updateSettings.mutateAsync({
        smtpHost,
        smtpPort: port,
        smtpUser,
        smtpPassword: smtpPassword || undefined,
        smtpSecure,
        senderName,
        senderEmail,
        isEnabled,
      });
      toast({ title: 'Email settings saved successfully' });
      setSmtpPassword('');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch {
      toast({ title: 'Failed to save email settings', variant: 'destructive' });
    }
  };

  const handleTestEmail = async () => {
    if (!testRecipient) {
      toast({ title: 'Please enter a test recipient email', variant: 'destructive' });
      return;
    }

    try {
      const result = await testEmail.mutateAsync(testRecipient);
      if (result.success) {
        toast({ title: result.message });
      } else {
        toast({ title: result.message, variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Failed to send test email', variant: 'destructive' });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          <CardTitle>Email / SMTP Settings</CardTitle>
        </div>
        <CardDescription>
          Configure SMTP settings for sending invite emails and notifications
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Configuration Status */}
          {settings && (
            <div className="flex items-center gap-2 text-sm">
              {settings.hasPassword ? (
                <div className="flex items-center gap-1.5 text-success">
                  <CheckCircle className="h-4 w-4" />
                  <span>SMTP configured</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-warning">
                  <AlertTriangle className="h-4 w-4" />
                  <span>SMTP password not set</span>
                </div>
              )}
              {settings.lastTestedAt && (
                <span className="text-muted-foreground ml-4">
                  Last test:{' '}
                  {settings.lastTestStatus === 'success' ? (
                    <span className="text-success">Success</span>
                  ) : (
                    <span className="text-danger">Failed</span>
                  )}
                </span>
              )}
            </div>
          )}

          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label>Enable Email Sending</Label>
              <p className="text-sm text-muted-foreground">
                When enabled, the system will send emails for invites and notifications
              </p>
            </div>
            <Switch
              checked={isEnabled}
              onCheckedChange={setIsEnabled}
              disabled={updateSettings.isPending}
            />
          </div>

          {/* SMTP Server Settings */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-muted-foreground">SMTP Server</h4>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtpHost">SMTP Host *</Label>
                <Input
                  id="smtpHost"
                  value={smtpHost}
                  onChange={(e) => setSmtpHost(e.target.value)}
                  placeholder="smtp.example.com"
                  disabled={updateSettings.isPending}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="smtpPort">SMTP Port *</Label>
                <Select value={smtpPort} onValueChange={setSmtpPort}>
                  <SelectTrigger id="smtpPort">
                    <SelectValue placeholder="Select port" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25 (SMTP)</SelectItem>
                    <SelectItem value="465">465 (SMTPS)</SelectItem>
                    <SelectItem value="587">587 (Submission)</SelectItem>
                    <SelectItem value="2525">2525 (Alternative)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtpUser">SMTP Username *</Label>
                <Input
                  id="smtpUser"
                  value={smtpUser}
                  onChange={(e) => setSmtpUser(e.target.value)}
                  placeholder="user@example.com"
                  disabled={updateSettings.isPending}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="smtpPassword">
                  SMTP Password {settings?.hasPassword ? '(leave blank to keep current)' : '*'}
                </Label>
                <div className="relative">
                  <Input
                    id="smtpPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={smtpPassword}
                    onChange={(e) => setSmtpPassword(e.target.value)}
                    placeholder={settings?.hasPassword ? '••••••••' : 'Enter password'}
                    disabled={updateSettings.isPending}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label>Use TLS/SSL</Label>
                <p className="text-xs text-muted-foreground">
                  Enable secure connection (recommended for ports 465 and 587)
                </p>
              </div>
              <Switch
                checked={smtpSecure}
                onCheckedChange={setSmtpSecure}
                disabled={updateSettings.isPending}
              />
            </div>
          </div>

          {/* Sender Settings */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-muted-foreground">Sender Information</h4>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="senderName">Sender Name *</Label>
                <Input
                  id="senderName"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="WeTicket"
                  disabled={updateSettings.isPending}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="senderEmail">Sender Email *</Label>
                <Input
                  id="senderEmail"
                  type="email"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  placeholder="noreply@example.com"
                  disabled={updateSettings.isPending}
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex items-center gap-3">
            <Button
              type="submit"
              disabled={updateSettings.isPending}
              className="gap-2"
            >
              {updateSettings.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : showSuccess ? (
                <>
                  <Check className="h-4 w-4" />
                  Saved
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Settings
                </>
              )}
            </Button>
          </div>

          {/* Test Email */}
          {settings?.hasPassword && (
            <div className="border-t pt-6 space-y-4">
              <h4 className="text-sm font-medium">Send Test Email</h4>

              <div className="flex gap-3">
                <div className="flex-1">
                  <Input
                    type="email"
                    value={testRecipient}
                    onChange={(e) => setTestRecipient(e.target.value)}
                    placeholder="recipient@example.com"
                    disabled={testEmail.isPending}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleTestEmail}
                  disabled={testEmail.isPending || !testRecipient}
                  className="gap-2"
                >
                  {testEmail.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send Test
                    </>
                  )}
                </Button>
              </div>

              {settings.lastTestedAt && (
                <p className="text-xs text-muted-foreground">
                  Last tested: {formatDisplayDateTime(settings.lastTestedAt)}
                  {settings.lastTestStatus === 'success' ? (
                    <CheckCircle className="inline h-3 w-3 ml-1 text-success" />
                  ) : (
                    <XCircle className="inline h-3 w-3 ml-1 text-danger" />
                  )}
                </p>
              )}
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
